import { minimatch } from "minimatch";
import { GitClient } from "../git/GitClient.js";
import { LockfileManager } from "../LockfileManager.js";
import { ReplayDetector } from "../ReplayDetector.js";
import { readFernignorePatterns } from "../shared/fernignore.js";
import type { StoredPatch } from "../types.js";

export interface ForgetOptions {
    /** Don't actually remove, just show what would be removed */
    dryRun?: boolean;
    /** Remove all tracked patches (keep lockfile and generation history) */
    all?: boolean;
    /** Specific patch IDs to remove */
    patchIds?: string[];
    /** Search pattern: file path, glob, or commit message substring */
    pattern?: string;
}

export interface DiffStat {
    additions: number;
    deletions: number;
}

export interface MatchedPatch {
    id: string;
    message: string;
    files: string[];
    diffstat: DiffStat;
    status?: "unresolved" | "resolving";
}

export interface ForgetResult {
    /** Whether replay is initialized (lockfile exists) */
    initialized: boolean;
    /** Patches that were (or would be in dry-run) removed */
    removed: MatchedPatch[];
    /** Number of patches remaining after removal */
    remaining: number;
    /** True if a search/pattern was given but nothing matched */
    notFound: boolean;
    /** Patch IDs that were specified but don't exist (idempotent mode) */
    alreadyForgotten: string[];
    /** Total patches before removal */
    totalPatches: number;
    /** Warnings (e.g., forgetting patches with conflict markers on disk) */
    warnings: string[];
    /** Matching patches for interactive selection (search/no-arg mode only) */
    matched?: MatchedPatch[];
}

function parseDiffStat(patchContent: string): DiffStat {
    let additions = 0;
    let deletions = 0;
    let inDiffHunk = false;
    for (const line of patchContent.split("\n")) {
        if (line.startsWith("diff --git ")) {
            inDiffHunk = true;
            continue;
        }
        if (!inDiffHunk) continue;
        if (line.startsWith("+") && !line.startsWith("+++")) {
            additions++;
        } else if (line.startsWith("-") && !line.startsWith("---")) {
            deletions++;
        }
    }
    return { additions, deletions };
}

function toMatchedPatch(patch: StoredPatch): MatchedPatch {
    return {
        id: patch.id,
        message: patch.original_message,
        files: patch.files,
        diffstat: parseDiffStat(patch.patch_content),
        ...(patch.status ? { status: patch.status } : {}),
    };
}

function matchesPatch(patch: StoredPatch, pattern: string): boolean {
    // File path match: exact or glob
    const fileMatch = patch.files.some(
        (file) => file === pattern || minimatch(file, pattern),
    );
    if (fileMatch) return true;

    // Commit message match: case-insensitive substring
    return patch.original_message.toLowerCase().includes(pattern.toLowerCase());
}

function buildWarnings(patches: StoredPatch[]): string[] {
    const warnings: string[] = [];
    for (const patch of patches) {
        if (patch.status === "resolving") {
            warnings.push(
                `patch ${patch.id} has conflict markers in these files: ${patch.files.join(", ")}. ` +
                `Run \`git checkout -- <files>\` to restore the generated versions.`,
            );
        } else if (patch.status === "unresolved") {
            warnings.push(
                `patch ${patch.id} had unresolved conflicts (files: ${patch.files.join(", ")}).`,
            );
        }
    }
    return warnings;
}

const EMPTY_RESULT: ForgetResult = {
    initialized: false,
    removed: [],
    remaining: 0,
    notFound: false,
    alreadyForgotten: [],
    totalPatches: 0,
    warnings: [],
};

/**
 * Record one dismissal per patch, tombstoning both the stored content hash
 * and (best effort) the re-emission hash recomputed from the original commit
 * through the exact detection pipeline. The stored hash drifts every time a
 * patch is carried forward across a clean regen, so it alone does not block
 * the detector from re-emitting the original commit when it re-enters the
 * scan range (e.g. after a regen PR is merged with a merge commit).
 *
 * Unreachable commits (shallow clone, GC, synthetic patches with no commit)
 * fall back to the stored hash silently — dismissal never fails.
 */
async function dismissPatches(
    outputDir: string,
    lockManager: LockfileManager,
    patches: StoredPatch[],
): Promise<void> {
    const git = new GitClient(outputDir);
    const detector = new ReplayDetector(git, lockManager, outputDir, readFernignorePatterns(outputDir));
    for (const patch of patches) {
        const hashes = [patch.content_hash];
        if (patch.original_commit) {
            const materialized = await detector.materializeCommitPatch(patch.original_commit);
            if (materialized.status === "ok" && !hashes.includes(materialized.contentHash)) {
                hashes.push(materialized.contentHash);
            }
        }
        lockManager.addDismissal({
            patch_id: patch.id,
            original_commit: patch.original_commit,
            original_message: patch.original_message,
            content_hashes: hashes,
            dismissed_at: new Date().toISOString(),
            via: "forget",
        });
    }
}

export async function forget(outputDir: string, options?: ForgetOptions): Promise<ForgetResult> {
    const lockManager = new LockfileManager(outputDir);

    if (!lockManager.exists()) {
        return { ...EMPTY_RESULT };
    }

    const lock = lockManager.read();
    const totalPatches = lock.patches.length;

    // --all: remove all patches
    if (options?.all) {
        const removed = lock.patches.map(toMatchedPatch);
        const warnings = buildWarnings(lock.patches);

        if (!options.dryRun) {
            await dismissPatches(outputDir, lockManager, lock.patches);
            lockManager.clearPatches();
            lockManager.save();
        }

        return {
            initialized: true,
            removed,
            remaining: 0,
            notFound: false,
            alreadyForgotten: [],
            totalPatches,
            warnings,
        };
    }

    // Patch ID mode: remove specific patches by ID
    if (options?.patchIds && options.patchIds.length > 0) {
        const removed: MatchedPatch[] = [];
        const alreadyForgotten: string[] = [];
        const patchesToRemove: StoredPatch[] = [];

        for (const id of options.patchIds) {
            const patch = lock.patches.find((p) => p.id === id);
            if (patch) {
                removed.push(toMatchedPatch(patch));
                patchesToRemove.push(patch);
            } else {
                alreadyForgotten.push(id);
            }
        }

        const warnings = buildWarnings(patchesToRemove);

        if (!options.dryRun) {
            await dismissPatches(outputDir, lockManager, patchesToRemove);
            for (const patch of patchesToRemove) {
                lockManager.removePatch(patch.id);
            }
            lockManager.save();
        }

        return {
            initialized: true,
            removed,
            remaining: totalPatches - removed.length,
            notFound: removed.length === 0 && alreadyForgotten.length > 0,
            alreadyForgotten,
            totalPatches,
            warnings,
        };
    }

    // Search mode: match by pattern (file path, glob, or commit message)
    if (options?.pattern) {
        const matched = lock.patches
            .filter((p) => matchesPatch(p, options.pattern!))
            .map(toMatchedPatch);

        return {
            initialized: true,
            removed: [],
            remaining: totalPatches,
            notFound: matched.length === 0,
            alreadyForgotten: [],
            totalPatches,
            warnings: [],
            matched,
        };
    }

    // No argument: return all patches for interactive selection
    return {
        initialized: true,
        removed: [],
        remaining: totalPatches,
        notFound: totalPatches === 0,
        alreadyForgotten: [],
        totalPatches,
        warnings: [],
        matched: lock.patches.map(toMatchedPatch),
    };
}
